import { c as _c } from "react/compiler-runtime";
import Typography, { type TypographyVariant } from '@precis-digital/kurama/Typography';
import Badge from '@precis-digital/kurama/Badge';
import DotsLoader from '@precis-digital/kurama/DotsLoader';
import Grid from '@precis-digital/kurama/Grid';
import Tooltip from '@precis-digital/kurama/Tooltip';
import { styled } from '@precis-digital/kurama/utils';
import React from 'react';
import { type ConfigStatus, type ErrorCodeType, type ErrorDetails, STATUSES, STATUSES_WITH_TOOLTIP, STATUS_COLOR_MAP } from 'shared/configStatuses';
import { type DateInput, formatDateTimeToAtFormat } from 'shared/dateFns';
import useDagView, { type DAGViewTypes } from 'shared/hooks/useDagView';
import { useTranslation } from 'shared/translations';
import StatusTooltipBody from './StatusTooltipBody';
import StatusTooltipTitle from './StatusTooltipTitle';
interface StatusBadgeProps {
  typographyVariant?: TypographyVariant;
  status: ConfigStatus;
  errorCode?: ErrorCodeType | null;
  lastUpdated?: DateInput;
  shouldShowLastUpdateInTitle?: boolean;
  dagViewInformation?: {
    dagViewType?: DAGViewTypes;
    configId?: number;
    configName?: string;
    isActive?: boolean;
    isPageWithConfigId?: boolean;
  };
  hasTooltip?: boolean;
  isLoading?: boolean;
  errorDetails?: ErrorDetails | string | null;
  statusMessage?: string | null;
  textAfterBadge?: string;
}
const StatusWithBadge = t0 => {
  const $ = _c(65);
  let errorCode;
  let errorDetails;
  let hasTooltip;
  let lastUpdated;
  let props;
  let status;
  let statusMessage;
  let t1;
  let t2;
  let t3;
  let t4;
  let textAfterBadge;
  if ($[0] !== t0) {
    ({
      typographyVariant: t1,
      status,
      errorCode,
      lastUpdated,
      shouldShowLastUpdateInTitle: t2,
      dagViewInformation: t3,
      isLoading: t4,
      hasTooltip,
      errorDetails,
      statusMessage,
      textAfterBadge,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = errorCode;
    $[2] = errorDetails;
    $[3] = hasTooltip;
    $[4] = lastUpdated;
    $[5] = props;
    $[6] = status;
    $[7] = statusMessage;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = textAfterBadge;
  } else {
    errorCode = $[1];
    errorDetails = $[2];
    hasTooltip = $[3];
    lastUpdated = $[4];
    props = $[5];
    status = $[6];
    statusMessage = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    textAfterBadge = $[12];
  }
  const typographyVariant = t1 === undefined ? "h4" : t1;
  const shouldShowLastUpdateInTitle = t2 === undefined ? true : t2;
  const dagViewInformation = t3 === undefined ? undefined : t3;
  const isLoading = t4 === undefined ? false : t4;
  const {
    t
  } = useTranslation("common");
  let t5;
  if ($[13] !== hasTooltip || $[14] !== status) {
    t5 = hasTooltip ?? STATUSES_WITH_TOOLTIP.includes(status);
    $[13] = hasTooltip;
    $[14] = status;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const showTooltip = t5;
  let t6;
  if ($[16] !== lastUpdated || $[17] !== t) {
    t6 = lastUpdated === undefined ? t("pending") : formatDateTimeToAtFormat(lastUpdated);
    $[16] = lastUpdated;
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const formattedLastUpdate = t6;
  const badgeColor = STATUS_COLOR_MAP[status];
  const {
    showDagView
  } = useDagView(dagViewInformation?.dagViewType, dagViewInformation?.isPageWithConfigId);
  let t7;
  if ($[19] !== dagViewInformation || $[20] !== showDagView || $[21] !== status) {
    t7 = () => {
      if (dagViewInformation == null) {
        return;
      }
      if (dagViewInformation.configId != null && dagViewInformation.configName != null && dagViewInformation.isActive != null) {
        showDagView({
          configId: dagViewInformation.configId,
          configName: dagViewInformation.configName,
          isActive: dagViewInformation.isActive,
          configStatus: status
        });
      }
    };
    $[19] = dagViewInformation;
    $[20] = showDagView;
    $[21] = status;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const openDagView = t7;
  let t8;
  if ($[23] !== openDagView || $[24] !== showTooltip || $[25] !== status) {
    t8 = e => {
      if (!showTooltip || status === STATUSES.pending) {
        return;
      }
      e.preventDefault();
      openDagView();
    };
    $[23] = openDagView;
    $[24] = showTooltip;
    $[25] = status;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const handleBadgeClick = t8;
  if (isLoading && status !== STATUSES.new) {
    const t9 = dagViewInformation !== undefined && status !== STATUSES.pending && showTooltip;
    let t10;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <DotsLoader height={24} width={36} />;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== t9) {
      t11 = <StyledGrid display="flex" alignItems="center" gap="10px" padding="8px 24px" pointyCursor={t9} hasBadge={false}>{t10}</StyledGrid>;
      $[28] = t9;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    return t11;
  }
  let t9;
  if ($[30] !== badgeColor || $[31] !== dagViewInformation || $[32] !== handleBadgeClick || $[33] !== props || $[34] !== showTooltip || $[35] !== status || $[36] !== t || $[37] !== typographyVariant) {
    t9 = () => <StyledGrid display="flex" alignItems="center" gap="4px" padding="8px 16px" pointyCursor={dagViewInformation !== undefined && status !== STATUSES.pending && showTooltip} hasBadge={badgeColor != null} {...props} onClick={e_0 => {
      handleBadgeClick(e_0);
    }}><StyledStatusTypography hasBadge={badgeColor != null} variant={typographyVariant}>{t(`status.${status}`)}</StyledStatusTypography>{badgeColor != null && <Badge color={badgeColor} />}</StyledGrid>;
    $[30] = badgeColor;
    $[31] = dagViewInformation;
    $[32] = handleBadgeClick;
    $[33] = props;
    $[34] = showTooltip;
    $[35] = status;
    $[36] = t;
    $[37] = typographyVariant;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const renderBadgeOnly = t9;
  if (!showTooltip) {
    let t10;
    if ($[39] !== renderBadgeOnly) {
      t10 = renderBadgeOnly();
      $[39] = renderBadgeOnly;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    let t11;
    if ($[41] !== textAfterBadge) {
      t11 = <Typography variant="body2">{textAfterBadge}</Typography>;
      $[41] = textAfterBadge;
      $[42] = t11;
    } else {
      t11 = $[42];
    }
    let t12;
    if ($[43] !== t10 || $[44] !== t11) {
      t12 = <StyledGridContainer>{t10}{t11}</StyledGridContainer>;
      $[43] = t10;
      $[44] = t11;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    return t12;
  }
  const t10 = !shouldShowLastUpdateInTitle;
  let t11;
  if ($[46] !== badgeColor || $[47] !== formattedLastUpdate || $[48] !== status || $[49] !== t10) {
    t11 = <StatusTooltipTitle status={status} formattedLastUpdate={formattedLastUpdate} badgeColor={badgeColor} shouldShowStatusMessageInTitle={t10} />;
    $[46] = badgeColor;
    $[47] = formattedLastUpdate;
    $[48] = status;
    $[49] = t10;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  const t12 = dagViewInformation != null;
  const t13 = errorDetails ?? null;
  let t14;
  if ($[51] !== errorCode || $[52] !== openDagView || $[53] !== shouldShowLastUpdateInTitle || $[54] !== status || $[55] !== statusMessage || $[56] !== t12 || $[57] !== t13) {
    t14 = <StatusTooltipBody status={status} hasLinkToDagView={t12} errorCode={errorCode} errorDetails={t13} statusMessage={statusMessage} openDagView={openDagView} shouldShowStatusMessageInBody={shouldShowLastUpdateInTitle} />;
    $[51] = errorCode;
    $[52] = openDagView;
    $[53] = shouldShowLastUpdateInTitle;
    $[54] = status;
    $[55] = statusMessage;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== renderBadgeOnly) {
    t15 = renderBadgeOnly();
    $[59] = renderBadgeOnly;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== t11 || $[62] !== t14 || $[63] !== t15) {
    t16 = <Tooltip title={t11} kind="multiline" body={t14} width="auto" maxWidth="400px">{t15}</Tooltip>;
    $[61] = t11;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  return t16;
};
const StyledStatusTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'hasBadge'
})<{
  hasBadge: boolean;
}>(({
  hasBadge,
  theme
}) => ({
  color: hasBadge ? theme.tokens.foreground.main.primary : theme.tokens.foreground.main.secondary
}));
const StyledGridContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
  width: 'fit-content'
}));
const StyledGrid = styled(Grid, {
  shouldForwardProp: prop => prop !== 'pointyCursor' && prop !== 'hasBadge'
})<{
  pointyCursor: boolean;
  hasBadge: boolean;
}>(({
  theme,
  pointyCursor,
  hasBadge
}) => ({
  backgroundColor: theme.tokens.background.tonal.soft.default,
  borderRadius: '16px',
  cursor: pointyCursor ? 'pointer' : 'default',
  width: 'fit-content',
  ...(hasBadge && {
    paddingRight: '8px'
  })
}));
export default StatusWithBadge;